import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import SVG from 'react-inlinesvg';
import { startsWith } from 'lodash';
import AppSidebar from './AppSidebar';
import AppTopbar from './AppTopbar';
import Ribbon from './Ribbon';
import { RibbonButton, RibbonSeparator } from './Ribbon';
import { LayoutProvider, useLayoutContext } from './LayoutContext';
import ContractPicker from '@/features/contract/ContractPicker';
import { RootState } from '@/store/types';
import { setMenuId } from '@/store/actions/typemark';
import { setPagePermission } from '@/store/actions/auth';
import { useNotificationsSocket } from '@/hooks/useNotificationsSocket';
import { useApprovalsSocket } from '@/features/approval/hooks/useApprovalsSocket';
import {
  approvalSummaryKey,
  useApprovalContext,
  useApprovalSummary,
} from '@/features/approval/hooks/useApprovals';
import { APPROVAL_ROUTES, isVisibleApprovalMenuItem } from '@/features/approval/navigation';
import type { ApprovalSummary } from '@/features/approval/types';

const AppLayoutInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { ribbonContent } = useLayoutContext();
  const queryClient = useQueryClient();
  const companyId = useSelector((state: any) => state.auth.current_company);
  // /approvals/context/ works for any verified employee — the admin-only company
  // detail is no longer loaded app-wide (it 403ed + toasted for non-admins).
  const { data: approvalContext } = useApprovalContext();
  const { data: approvalSummary } = useApprovalSummary(
    !!approvalContext?.enabled && !!approvalContext?.is_admin
  );

  useNotificationsSocket();

  // Realtime schvalovací workflow — snapshot počtů drží react-query cache
  // (`['approval-summary']`), ze které čte badge ve frontě žádostí.
  const handleApprovalSnapshot = useCallback(
    (counts: ApprovalSummary) => {
      queryClient.setQueryData(approvalSummaryKey(companyId), counts);
    },
    [companyId, queryClient]
  );
  useApprovalsSocket(companyId, handleApprovalSnapshot);

  const menu = useSelector((state: RootState) => (state as any).menu?.menu);
  const { contract, modelId, phaseId } = useSelector((state: any) => state.contract);
  const contractId = contract?.id ?? null;

  const pathname = location.pathname;

  // menu_1 → sidebar items
  const menu1 = menu?.menu_1 || menu?.menu1 || [];

  // menu_2 → topbar tabs (filtered by current route_1)
  const menu2 = menu?.menu_2 || menu?.menu2 || [];
  const topTabs = useMemo(() => {
    return menu2
      .filter(
        (item: any) =>
          pathname.includes(item.route_1) && isVisibleApprovalMenuItem(item, approvalContext)
      )
      .map((item: any) => ({
        value: item.route_1 + item.route_2,
        label: item.title,
      }));
  }, [menu2, pathname, approvalContext]);

  const activeTab = useMemo(() => {
    // Find the most specific matching tab
    let best = '';
    for (const tab of topTabs) {
      if (pathname.startsWith(tab.value) && tab.value.length > best.length) {
        best = tab.value;
      }
    }
    return best || undefined;
  }, [topTabs, pathname]);

  // menu_3 → ribbon navigation items (filtered by matching route_1 + route_2)
  const menu3 = menu?.menu_3 || menu?.menu3 || [];
  const menu4 = menu?.menu_4 || menu?.menu4 || [];

  const ribbonNavItems = useMemo(() => {
    return menu3
      .filter((item: any) => isVisibleApprovalMenuItem(item, approvalContext))
      .map((item: any) => {
        const baseRoute = item.route_1 + item.route_2;
        if (!startsWith(pathname, baseRoute)) return null;

        // Build full route (include menu_4 if present)
        const matching4 = menu4.filter(
          (m4: any) =>
            item.route_1 + item.route_2 + item.route_3 === m4.route_1 + m4.route_2 + m4.route_3
        );
        const route =
          matching4.length > 0
            ? baseRoute + item.route_3 + matching4[0].route_4
            : baseRoute + item.route_3;

        return {
          ...item,
          route,
          routeForActive: baseRoute + item.route_3,
          children: matching4.map((child: any) => ({
            ...child,
            id: item.id,
            status: item.status,
            route: baseRoute + item.route_3 + child.route_4,
          })),
        };
      })
      .filter(Boolean);
  }, [menu3, menu4, pathname, approvalContext]);

  // Menu routes may carry a query (e.g. `/vzhledy?library=patterns`); an item is
  // active only when every query param of its route matches the current URL
  // (`library` falls back to `appearances` when absent).
  const isRouteActive = useCallback(
    (route: string) => {
      const target = new URL(route, window.location.origin);
      if (!startsWith(pathname, target.pathname)) return false;
      const currentQuery = new URLSearchParams(location.search);
      return [...target.searchParams].every(
        ([key, value]) =>
          (currentQuery.get(key) ?? (key === 'library' ? 'appearances' : null)) === value
      );
    },
    [pathname, location.search]
  );

  // Is in akce section and needs contract?
  const isDashboard = pathname === '/app/dashboard' || pathname === '/app';
  const isAkce = pathname.startsWith('/app/akce');
  const isDisabledNav = isAkce && !(contractId && modelId && phaseId);

  const handleSection = (route: string) => {
    history.push(route);
  };

  const handleTab = (route: string) => {
    history.push(route);
  };

  const handleRibbonNavClick = (item: any) => {
    if (item.id) {
      dispatch(setMenuId(item.id));
    }
    if (item.status != null) {
      dispatch(setPagePermission(item.status));
    }
    history.push(item.route);
  };

  // Combine ribbon: API nav items + page-specific actions
  const hasRibbonNav = ribbonNavItems.length > 0;
  const hasRibbonContent = ribbonContent || hasRibbonNav;

  return (
    <div className="flex h-screen overflow-hidden bg-surface-bg">
      <AppSidebar menuItems={menu1} activeRoute={pathname} onNavigate={handleSection} />
      <div className="flex-1 flex flex-col min-w-0">
        {!isDashboard && (
          <AppTopbar
            tabs={topTabs}
            tab={activeTab}
            onTab={handleTab}
            contractPicker={isAkce ? <ContractPicker /> : undefined}
          />
        )}
        {hasRibbonContent && (
          <Ribbon>
            {ribbonNavItems.map((item: any, i: number) => {
              const isActive = isRouteActive(item.routeForActive);
              const isItemDisabled =
                isDisabledNav && item.route !== '/app/akce/sprava/faze/vykonove-faze';
              return (
                <RibbonButton
                  key={i}
                  icon={
                    item.icon_svg ? (
                      <SVG src={item.icon_svg} className="h-6 w-6 fill-current" />
                    ) : null
                  }
                  label={item.title}
                  count={
                    item.route === APPROVAL_ROUTES.queue
                      ? approvalSummary?.pending_count
                      : undefined
                  }
                  countLabel="Čekající žádosti v celé firmě"
                  active={isActive}
                  disabled={isItemDisabled}
                  onClick={() => !isItemDisabled && handleRibbonNavClick(item)}
                  items={item.children.map((child: any) => ({
                    key: child.route,
                    label: child.title,
                    active: isRouteActive(child.route),
                    onSelect: () => !isItemDisabled && handleRibbonNavClick(child),
                  }))}
                />
              );
            })}
            {hasRibbonNav && ribbonContent && <RibbonSeparator />}
            {ribbonContent}
          </Ribbon>
        )}
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden relative z-[1]">
          {children}
        </main>
      </div>
    </div>
  );
};

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isLoggedIn);

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <LayoutProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </LayoutProvider>
  );
};

export default AppLayout;
